import type { Dayjs } from "dayjs";

export const GLOBAL_SEARCH_DATE_KEYWORDS = [
  "today",
  "yesterday",
  "tomorrow",
  "past",
  "future",
  "thisweek",
  "lastweek",
  "nextweek",
  "thismonth",
] as const;

export type GlobalSearchDateKeyword = (typeof GLOBAL_SEARCH_DATE_KEYWORDS)[number];

export type GlobalSearchDateRange = { after?: string; before?: string };

export type ParsedGlobalSearchDates = {
  textQuery: string;
  dateRange: GlobalSearchDateRange | null;
  matchedKeyword: GlobalSearchDateKeyword | null;
};

export type DetectedGlobalSearchKeyword = {
  textQuery: string;
  matchedKeyword: GlobalSearchDateKeyword | null;
};

export function keywordToRange(kw: GlobalSearchDateKeyword, now: Dayjs): GlobalSearchDateRange {
  switch (kw) {
    case "today":
      return {
        after: now.startOf("day").toISOString(),
        before: now.add(1, "day").startOf("day").toISOString(),
      };
    case "yesterday": {
      const y = now.subtract(1, "day");
      return {
        after: y.startOf("day").toISOString(),
        before: y.add(1, "day").startOf("day").toISOString(),
      };
    }
    case "tomorrow": {
      const t = now.add(1, "day");
      return {
        after: t.startOf("day").toISOString(),
        before: t.add(1, "day").startOf("day").toISOString(),
      };
    }
    case "past":
      return { before: now.toISOString() };
    case "future":
      return { after: now.toISOString() };
    case "thisweek": {
      const start = now.startOf("week");
      return {
        after: start.toISOString(),
        before: start.add(1, "week").toISOString(),
      };
    }
    case "lastweek": {
      const thisWeekStart = now.startOf("week");
      const lastWeekStart = thisWeekStart.subtract(1, "week");
      return {
        after: lastWeekStart.toISOString(),
        before: thisWeekStart.toISOString(),
      };
    }
    case "nextweek": {
      const nextWeekStart = now.startOf("week").add(1, "week");
      return {
        after: nextWeekStart.toISOString(),
        before: nextWeekStart.add(1, "week").toISOString(),
      };
    }
    case "thismonth":
      return {
        after: now.startOf("month").toISOString(),
        before: now.add(1, "month").startOf("month").toISOString(),
      };
    default:
      return {};
  }
}

export function detectGlobalSearchDateKeyword(input: string): DetectedGlobalSearchKeyword {
  const trimmed = input.trim();
  if (!trimmed) return { textQuery: "", matchedKeyword: null };

  const tokens = trimmed.split(/\s+/);
  let matchedKeyword: GlobalSearchDateKeyword | null = null;
  const rest: string[] = [];

  for (const t of tokens) {
    const lower = t.toLowerCase();
    if (!matchedKeyword && (GLOBAL_SEARCH_DATE_KEYWORDS as readonly string[]).includes(lower)) {
      matchedKeyword = lower as GlobalSearchDateKeyword;
      continue;
    }
    rest.push(t);
  }

  return { textQuery: rest.join(" ").trim(), matchedKeyword };
}

export function parseGlobalSearchDateKeywords(input: string, now: Dayjs): ParsedGlobalSearchDates {
  const { textQuery, matchedKeyword } = detectGlobalSearchDateKeyword(input);
  if (!matchedKeyword) {
    return { textQuery: input.trim(), dateRange: null, matchedKeyword: null };
  }
  return {
    textQuery,
    dateRange: keywordToRange(matchedKeyword, now),
    matchedKeyword,
  };
}

export function formatGlobalSearchDateKeywordLabel(k: GlobalSearchDateKeyword): string {
  switch (k) {
    case "today":
      return "Today";
    case "yesterday":
      return "Yesterday";
    case "tomorrow":
      return "Tomorrow";
    case "past":
      return "Past";
    case "future":
      return "Future";
    case "thisweek":
      return "This week";
    case "lastweek":
      return "Last week";
    case "nextweek":
      return "Next week";
    case "thismonth":
      return "This month";
    default:
      return k;
  }
}
